<template>
  <div
    id="performance"
    class="px-4"
  >
    <v-container
      v-if="(!events.data && events.loading) || (eventDetails.loading && (eventDetails.selectedPeriodId === undefined || eventDetails.selectedPeriodId === 0) && !eventDetails.selectedExercise)"
      class="d-flex justify-center align-center my-16"
    >
      <v-progress-circular
        indeterminate
        size="100"
      />
    </v-container>
    <div v-else>
      <v-row
        key="performancePortalFilter"
        align="center"
        class="mt-0"
      >
        <v-col
          v-if="eventParentDetails.data === undefined"
          class="ml-3 my-4"
        >
          <span class="text-h6">
            {{ $t("event_analysis.no_events") }}
            <v-btn
              class="ml-2 text-none"
              small
              outlined

              @click="eventSelection = true"
            >
              <v-icon
                x-small
                left
              >
                far fa-pen
              </v-icon>
              {{ $t("button.change_period") }}
            </v-btn>
          </span>
        </v-col>
        <v-col
          v-else
        >
          <span
            class="text-h6 font-weight-medium text-none primary--text"
            style="cursor: pointer;"
            @click="eventSelection = true"
          >
            {{ eventParentDetails.data.title }}
            <v-icon
              x-small
              color="primary"
            >fa fa-chevron-down</v-icon>
          </span>
          <v-row
            align="center"
            no-gutters
          >
            <v-col cols="auto">
              {{ dayjs(eventParentDetails.data.start_at).format('dddd, MMMM D HH:mm') }} - {{ dayjs(eventParentDetails.data.end_at).format('HH:mm') }}
            </v-col>
            <v-col
              cols="auto"
              class="pl-3"
            >
              <v-chip
                label
                :color="eventTypes[eventParentDetails.data.event_type].color"
                dark
                small
              >
                {{ eventTypes[eventParentDetails.data.event_type].text }}
              </v-chip>
            </v-col>
            <v-col cols="auto">
              <v-btn
                small
                text
                color="primary"
                class="mr-2 text-none"
                @click="eventParentDetails.drawer = !eventParentDetails.drawer;"
              >
                {{ $t('button.view_event_details') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
        <v-col
          v-if="hasPeriods || hasTrainingExercisesStartTime"
          cols="auto"
          class="text-body-2 pr-0"
        >
          <v-btn
            class="text-none mr-1"
            color="primary"
            text
            @click="periodSelection = true"
          >
            {{ buttonLabel }}
            <v-icon
              right
              x-small
            >
              far fa-chevron-down
            </v-icon>
          </v-btn>
        </v-col>
        <div v-if="!isIndividual">
          <v-col
            v-if="$vuetify.breakpoint.xlOnly"
            cols="auto"
          >
            <v-btn
              v-for="view in views"
              :key="view.name"
              :value="view.name"
              :outlined="currentView !== view.name"
              :color="currentView === view.name ? 'primary' : ''"
              :text="currentView !== view.name"
              class="mx-1"
              @click="currentView = view.name"
            >
              {{ view.title }}
            </v-btn>
          </v-col>

          <v-col
            v-else
            cols="auto"
          >
            <v-select
              v-model="currentView"
              solo
              dense
              hide-details
              color="primary"
              item-value="name"
              item-text="title"
              :items="views"
              style="width: 150px"
            />
          </v-col>
        </div>
        <v-col
          v-if="!isIndividual"
          cols="auto"
        >
          <v-btn
            icon
            @click="customizeModal = !customizeModal"
          >
            <v-icon
              small
            >
              fa-light fa-gear
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>
      <v-divider class="mt-3" />
      <v-alert
        v-if="eventDetails.data && eventDetails.data.waiting_for_stats"
        prominent
        text
        outlined
        color="primary"
        class="mt-6 text-body-1"
      >
        <v-progress-circular
          :size="40"
          indeterminate
          :width="2"
          class="ml-4 mr-8"
        />
        {{ $t("performance.event.waiting_for_stats") }}
      </v-alert>

      <div v-if="isIndividual">
        <performance-individual
          v-if="eventDetails.data && eventDetails.data.players.length > 0"
          :key="eventDetails.data.id + ' ' + eventDetails.data.players[0].id"
          class="mt-4"
          :player="cloneDeep(eventDetails.data.players[0])"
          :team="eventParentDetails.data.teams[0]"
          :event="eventParentDetails"
          :exercise="eventDetails.selectedExercise"
        />
      </div>
      <div v-else>
        <div v-if="currentView === 'SUMMARY'">
          <v-row
            align="center"
            class="mt-2"
          >
            <v-col
              v-for="variable in statCardsSUM"
              :key="variable.id"
            >
              <stat-card
                :loading="eventDetails.loading"
                :title="variable.text"
                :value="currentTeamStats[variable.value]"
                :max="performanceMaxValues[variable.value] || eventDetails.maxValues[variable.value]"
                :color="getColorFromValue(currentTeamStats[variable.value], performanceMaxValues[variable.value] || eventDetails.maxValues[variable.value], reversedVariables().includes(variable.value))"
              />
            </v-col>
          </v-row>
        </div>
        <div v-else-if="currentView === 'RPE'">
          <v-row
            align="center"
            class="mt-2"
          >
            <v-col
              v-for="variable in statCardsRPE"
              :key="variable.id"
            >
              <stat-card
                v-if="variable.value === 'feedback'"
                :title="variable.text"
                :value="(eventDetails.data.feedback_requests.filter(f => f.submitted_at).length / eventDetails.data.players.length) * 100"
                :max="100"
                :color="getColorFromValue((eventDetails.data.feedback_requests.filter(f => f.submitted_at).length / eventDetails.data.players.length) * 100, 100)"
              >
                {{ Math.ceil((eventDetails.data.feedback_requests.filter(f => f.submitted_at).length / eventDetails.data.players.length) * 100) }}%
              </stat-card>
              <stat-card
                v-else-if="variable.value === 'reason'"
                :title="variable.text"
                :value="(eventDetails.data.players.filter(p => p.has_attended).length / eventDetails.data.players.length) * 100"
                :max="100"
                :color="getColorFromValue((eventDetails.data.players.filter(p => p.has_attended).length / eventDetails.data.players.length) * 100, 100)"
              >
                {{ Math.ceil((eventDetails.data.players.filter(p => p.has_attended).length / eventDetails.data.players.length) * 100) }}%
              </stat-card>
              <stat-card
                v-else
                :loading="eventDetails.loading"
                :title="variable.text"
                :value="currentTeamStats[variable.value]"
                :max="performanceMaxValues[variable.value] || eventDetails.maxValues[variable.value]"
                :color="getColorFromValue(currentTeamStats[variable.value], performanceMaxValues[variable.value] || eventDetails.maxValues[variable.value], reversedVariables().includes(variable.value))"
              />
            </v-col>
          </v-row>
        </div>
        <div v-else-if="currentView === 'GPS' || periodSummary">
          <v-row
            align="center"
            class="mt-2"
          >
            <v-col
              v-for="variable in statCardsGPS"
              :key="variable.id"
            >
              <stat-card
                :loading="eventDetails.loading"
                :title="variable.text"
                :value="currentTeamStats[variable.value]"
                :max="performanceMaxValues[variable.value] || eventDetails.maxValues[variable.value]"
                :color="getColorFromValue(currentTeamStats[variable.value], performanceMaxValues[variable.value] || eventDetails.maxValues[variable.value], reversedVariables().includes(variable.value))"
              />
            </v-col>
          </v-row>
        </div>
        <div v-else-if="currentView === 'HR'">
          <v-row
            align="center"
            class="mt-2"
          >
            <v-col
              v-for="variable in statCardsHR"
              :key="variable.id"
            >
              <stat-card
                :loading="eventDetails.loading"
                :title="variable.text"
                :value="currentTeamStats[variable.value]"
                :max="performanceMaxValues[variable.value] || eventDetails.maxValues[variable.value]"
                :color="getColorFromValue(currentTeamStats[variable.value], performanceMaxValues[variable.value] || eventDetails.maxValues[variable.value], reversedVariables().includes(variable.value))"
              />
            </v-col>
          </v-row>
        </div>
        <div v-else-if="currentView === 'RR'">
          <v-row
            align="center"
            class="mt-2"
          >
            <v-col
              v-for="variable in statCardsRR"
              :key="variable.id"
            >
              <stat-card
                :loading="eventDetails.loading"
                :title="variable.text"
                :value="currentTeamStats[variable.value]"
                :max="performanceMaxValues[variable.value] || eventDetails.maxValues[variable.value]"
                :color="getColorFromValue(currentTeamStats[variable.value], performanceMaxValues[variable.value] || eventDetails.maxValues[variable.value], reversedVariables().includes(variable.value))"
              />
            </v-col>
          </v-row>
        </div>

        <section
          class="performance__tables mb-6"
        >
          <v-card class="mt-6">
            <v-card-title class="px-6 py-4">
              <v-row
                v-if="periodSummary"
                no-gutters
                align="center"
              >
                <v-col>
                  <div class="subtitle-2 grey--text text--grey-100">
                    {{ $t('card_title.period_summary') }}
                  </div>
                </v-col>
                <v-col
                  cols="auto"
                  class="pl-2"
                >
                  <variable-selector
                    v-if="variables.data"
                    :variables="variables.data"
                    :dashboard="DashboardType.EVENT_PERFORMANCE"
                    category="SUMMARY"
                  />
                </v-col>
              </v-row>
              <v-row
                v-else
                no-gutters
                align="center"
              >
                <v-col>
                  <div class="subtitle-2 grey--text text--grey-100">
                    {{ $t('event_details.players') }}
                  </div>
                </v-col>
                <template>
                  <v-col
                    cols="auto"
                    class="pl-2"
                  >
                    <v-text-field
                      v-model="playerName"
                      solo
                      dense
                      :label="$t('table_headers.name')"
                      hide-details
                      clearable
                      style="width: 200px"
                    />
                  </v-col>
                  <v-col
                    cols="auto"
                    class="pl-2"
                  >
                    <v-select
                      v-model="playerPositions.selectedId"
                      solo
                      dense
                      :label="$t('table_headers.player_overview.position')"
                      hide-details
                      clearable
                      item-value="id"
                      item-text="name"
                      :items="playerPositions.data"
                      style="width: 200px"
                    />
                  </v-col>
                  <v-col
                    cols="auto"
                    class="pl-2"
                  >
                    <v-select
                      v-model="playerStatuses.selectedId"
                      solo
                      dense
                      :label="$t('table_headers.player_overview.status')"
                      hide-details
                      clearable
                      :items="playerStatuses.data"
                      item-value="id"
                      item-text="name"
                      style="width: 200px"
                    />
                  </v-col>
                  <v-col
                    cols="auto"
                    class="pl-2"
                  >
                    <variable-selector
                      v-if="variables.data"
                      :variables="variables.data"
                      :dashboard="DashboardType.EVENT_PERFORMANCE"
                      :category="currentView"
                    />
                  </v-col>
                </template>
              </v-row>
            </v-card-title>
            <v-card-text class="pa-0">
              <div v-if="periodSummary">
                <performance-period-table
                  :table-headers="tableHeadersSUM"
                  :periods="eventPeriods.data"
                  :loading="eventPeriods.loading"
                  :max-values="eventPeriods.maxValues"
                />
              </div>
              <div v-else-if="currentView === 'SUMMARY'">
                <performance-rpe-table
                  v-if="eventDetails.data"
                  :max-values="eventDetails.maxValues"
                  :player-performance="eventDetails.data ? searchFilteredPlayers : []"
                  :table-headers="tableHeadersSUM"
                  :colors-max-values="performanceMaxValues"
                  @select:player="player.selected = $event; eventParentDetails.drawer = false; playerIndividualDialog = true"
                />
              </div>
              <div v-else-if="currentView === 'RPE'">
                <performance-rpe-table
                  v-if="eventDetails.data"
                  :max-values="eventDetails.maxValues"
                  :player-performance="eventDetails.data ? searchFilteredPlayers : []"
                  :table-headers="tableHeadersRPE"
                  :colors-max-values="performanceMaxValues"
                  @select:player="player.selected = $event; eventParentDetails.drawer = false; playerIndividualDialog = true"
                />
              </div>
              <div v-else-if="currentView === 'GPS'">
                <performance-general-table
                  :event-table="true"
                  :player-performance="searchFilteredPlayers"
                  :loading="eventDetails.loading"
                  :max-values="eventDetails.maxValues"
                  :table-headers="tableHeadersGPS"
                  :colors-max-values="performanceMaxValues"
                  @select:player="player.selected = $event; eventParentDetails.drawer = false; playerIndividualDialog = true"
                  @open:raw="player.rawSelected = $event; rawDialog = true"
                />
              </div>
              <div v-else-if="currentView === 'HR'">
                <performance-heart-rate-table
                  v-if="eventDetails.data"
                  :max-values="eventDetails.maxValues"
                  :player-performance="eventDetails.data ? searchFilteredPlayers : []"
                  :table-headers="tableHeadersHR"
                  :colors-max-values="performanceMaxValues"
                  @select:player="player.selected = $event; eventParentDetails.drawer = false; playerIndividualDialog = true"
                  @open:raw="player.rawSelected = $event; rawDialog = true"
                />
              </div>
              <div v-else-if="currentView === 'RR'">
                <performance-r-r-table
                  v-if="eventDetails.data"
                  :max-values="eventDetails.maxValues"
                  :player-performance="eventDetails.data ? searchFilteredPlayers : []"
                  :table-headers="tableHeadersRR"
                  @select:player="player.selected = $event; eventParentDetails.drawer = false; playerIndividualDialog = true"
                />
              </div>
            </v-card-text>
          </v-card>
        </section>

        <div v-if="periodSummary">
          <performance-period-graphs
            :periods="eventPeriods.data"
          />
        </div>
        <div v-else-if="currentView === 'GPS'">
          <performance-graphs
            v-if="eventDetails.data"
            :players="searchFilteredPlayers"
          />
        </div>
        <div v-else-if="currentView === 'HR'">
          <performance-heart-rate-graphs
            v-if="eventDetails.data"
            :player-performance="searchFilteredPlayers"
            :event-data="eventDetails.data"
            :loading-raw-heart-rate="player.loadingRawData"
          />
        </div>
        <div v-else-if="currentView === 'RR'">
          <performance-r-r-graphs
            :player-performance="searchFilteredPlayers"
          />
        </div>
        <div v-else-if="currentView === 'SUMMARY'">
          <performance-summary-graphs
            :players="searchFilteredPlayers"
            :team="currentTeamStats"
          />
        </div>
        <div v-else-if="currentView === 'RPE'">
          <performance-rpe-graphs
            :players="searchFilteredPlayers"
            :team="currentTeamStats"
            :event="eventDetails.data"
          />
        </div>

        <performance-raw-dialog
          v-if="player.rawSelected && eventDetails.data"
          :player="player.rawSelected"
          :visible="rawDialog"
          :event="eventDetails.data"
          :loading-raw-data="player.loadingRawData"
          @update:visible="rawDialog = false"
          @previous="selectPrevious"
          @next="selectNext"
        />
      </div>
    </div>

    <performance-coach-customize
      :visible.sync="customizeModal"
      :dashboard="dashboard"
      :view="currentView"
      @save="fetchVariables"
    />

    <event-sidebar
      v-if="eventParentDetails.data"
      :event-details.sync="eventParentDetails.data"
      :loading="eventParentDetails.loading"
      :value.sync="eventParentDetails.drawer"
      :event-view="false"
      @update="fetchEventDetails(eventDetails.data.id, false)"
    />

    <card-dialog
      v-model="playerIndividualDialog"
      :persistent="false"
      :title="$t('card_dialog.performance_individual.title')"
      max-width="1300px"
    >
      <v-card-text
        class="pa-6 grey-bg lighten-2"
      >
        <performance-individual
          v-if="eventDetails.data && player.selected"
          :key="eventParentDetails.data.id + ' ' + player.selected.id"
          :event="eventParentDetails"
          :team="currentTeam"
          :player="player.selected"
          :single-player-event="false"
          :exercise="eventDetails.selectedExercise"
        />
      </v-card-text>
    </card-dialog>

    <card-dialog
      v-model="periodSelection"
      :value="periodSelection"
      :persistent="false"
      max-width="700px"
    >
      <template v-slot:title>
        <v-card-title
          class="blue-grey lighten-5 px-6 py-4"
        >
          <div>
            {{ $t("card_dialog.select_period.title") }}
          </div>
        </v-card-title>
      </template>

      <v-card-text class="pa-0">
        <div class="pa-4">
          <v-list
            v-if="eventParentDetails.data"
            class="v-list--bordered ma-0 pa-0"
          >
            <v-list-item
              :class="{ 'v-list-item--active': eventDetails.selectedPeriodId === undefined && !eventDetails.selectedExercise }"
              @click="eventDetails.selectedPeriodId = 0; periodSelection = false"
            >
              <v-list-item-content>
                <v-list-item-title
                  :class="{ 'font-weight-bold primary--text': eventDetails.selectedPeriodId === undefined && !eventDetails.selectedExercise }"
                >
                  {{ $t('card_dialog.select_period.entire_event', {event_type: eventTypes[eventParentDetails.data.event_type].text}) }}
                </v-list-item-title>
                <v-list-item-subtitle class="mt-1">
                  {{ $dayjs(eventParentDetails.data.start_at).format('ddd, MMMM D, HH:mm') }} - {{ $dayjs(eventParentDetails.data.end_at).format('HH:mm') }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <v-btn-toggle
            v-if="hasPeriods && hasTrainingExercisesStartTime"
            v-model="advancedView"
            class="v-btn-toggle--block my-2"
            mandatory
            color="primary"
          >
            <v-btn value="period">
              {{ $t('button.period') }}
            </v-btn>
            <v-btn value="exercise">
              {{ $t('button.exercise') }}
            </v-btn>
          </v-btn-toggle>
          <v-divider v-else />
          <div v-if="advancedView === 'period'">
            <v-list
              v-if="eventParentDetails.data && eventParentDetails.data.periods.length > 0"
              class="v-list--bordered ma-0 pa-0"
            >
              <v-list-item
                v-if="!isIndividual"
                :class="{ 'v-list-item--active': eventDetails.selectedPeriodId && eventDetails.selectedPeriodId === -1 }"
                @click="eventDetails.selectedPeriodId = -1; periodSelection = false; currentView = 'SUMMARY'"
              >
                <v-list-item-content>
                  <v-list-item-title
                    :class="{ 'font-weight-bold primary--text': eventDetails.selectedPeriodId && eventDetails.selectedPeriodId === -1 }"
                  >
                    {{ $t("card_title.period_summary") }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="mt-1">
                    {{ $dayjs(eventParentDetails.data.start_at).format('ddd, MMMM D, HH:mm') }} - {{ $dayjs(eventParentDetails.data.end_at).format('HH:mm') }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-divider />
              <v-list-item
                v-for="period in eventParentDetails.data.periods"
                :key="period.id"
                :class="{ 'v-list-item--active': eventDetails.selectedPeriodId && eventDetails.selectedPeriodId === period.id }"
                @click="eventDetails.selectedPeriodId = period.id; periodSelection = false"
              >
                <v-list-item-content>
                  <v-list-item-title
                    :class="{ 'font-weight-bold primary--text': eventDetails.selectedPeriodId && eventDetails.selectedPeriodId === period.id }"
                  >
                    {{ period.title }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="mt-1">
                    {{ $dayjs(period.start_at).format('ddd, MMMM D, HH:mm') }} - {{ $dayjs(period.end_at).format('HH:mm') }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>
          <div v-else>
            <v-list
              v-if="eventParentDetails.data && eventParentDetails.data.training_exercises.length > 0"
              class="v-list--bordered ma-0 pa-0"
            >
              <div
                v-for="exercise in eventTrainingExercises"
                :key="exercise.id"
              >
                <div v-if="exercise.type === 'superset' || exercise.type === 'pause' || exercise.type === 'exercise' ">
                  <exercise-list-item
                    :exercise="exercise"
                    :active="eventDetails.selectedExercise && eventDetails.selectedExercise.id === exercise.id"
                    @handle:click="eventDetails.selectedExercise = exercise; periodSelection = false"
                  />
                </div>
                <div v-else>
                  <div
                    v-for="subExercise in exercise.exercises"
                    :key="subExercise.id"
                  >
                    <exercise-list-item
                      :exercise="subExercise"
                      :active="eventDetails.selectedExercise && eventDetails.selectedExercise.id === exercise.id"
                    />
                  </div>
                </div>
              </div>
            </v-list>
          </div>
        </div>
      </v-card-text>
    </card-dialog>

    <card-dialog
      v-model="eventSelection"
      :value="eventSelection"
      :persistent="false"
      :title="$t('select_event.label')"
      max-width="700px"
    >
      <template v-slot:title>
        <v-card-title
          class="blue-grey lighten-5 px-6 py-4"
        >
          <div>
            {{ $t("select_event.label") }}
          </div>
          <v-row
            class="mt-3"
            dense
          >
            <v-col>
              <v-text-field
                v-model="filters.eventName"
                solo
                dense
                :label="$t('table_headers.name')"
                hide-details
                clearable
              />
            </v-col>
            <v-col>
              <v-select
                v-model="filters.eventType"
                solo
                dense
                :label="$t('dropdown.event_type.title')"
                hide-details
                clearable
                item-value="type"
                item-text="name"
                :items="[{type: eventTypeTraining, name: $t('event_type.training')}, {type: eventTypeMatch, name: $t('event_type.match')}]"
              />
            </v-col>
            <v-col>
              <v-menu
                v-model="filterDateFromActive"
                :close-on-content-click="true"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :value="dateFromText"
                    class="custom-height-text-field"
                    :label="$t('date_from.label')"
                    solo
                    dense
                    clearable
                    hide-details
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="filters.dateFrom"
                  :first-day-of-week="1"
                  no-title
                  :max="$dayjs().format('YYYY-MM-DD')"
                  @change="filterDateFromActive = false; events.pagination.current_page = 1; fetchEvents(false)"
                />
              </v-menu>
            </v-col>
            <v-col>
              <v-menu
                v-model="filterDateToActive"
                :close-on-content-click="true"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :value="dateToText"
                    class="custom-height-text-field"
                    :label="$t('date_to.label')"
                    solo
                    dense
                    clearable
                    hide-details
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="filters.dateTo"
                  :first-day-of-week="1"
                  no-title
                  :max="$dayjs().format('YYYY-MM-DD')"
                  @change="filterDateToActive = false; events.pagination.current_page = 1; fetchEvents(false)"
                />
              </v-menu>
            </v-col>
          </v-row>
        </v-card-title>
      </template>

      <template v-slot:actions>
        <v-card-actions
          v-if="events.pagination && events.pagination.last_page > 1"
          class="d-flex justify-center align-center py-5"
        >
          <pagination
            v-model="events.pagination.current_page"
            :last-page="events.pagination.last_page"
          />
        </v-card-actions>
      </template>
      <v-card-text class="pa-0">
        <div style="height: 1px">
          <v-progress-linear
            v-if="events.loading"
            indeterminate
            height="1px"
          />
        </div>

        <div class="pa-4">
          <v-list
            v-if="events.data && events.data.length > 0"
            class="v-list--bordered ma-0 pa-0"
          >
            <v-list-item
              v-for="event in events.data"
              :key="event.id"
              :class="{ 'v-list-item--active': events.selectedEvent && events.selectedEvent.id === event.id }"
              @click="events.selectedEvent = event; currentView = 'SUMMARY'; eventSelection = false"
            >
              <v-list-item-content>
                <v-list-item-title
                  :class="{ 'font-weight-bold primary--text': events.selectedEvent && events.selectedEvent.id === event.id }"
                >
                  {{ event.title }}
                </v-list-item-title>
                <v-list-item-subtitle class="mt-1">
                  {{ $dayjs(event.start_at).format('ddd, MMMM D, HH:mm') }}
                  &middot;
                  {{ event.teams.map(t => t.short_name).join(', ') }}
                  &middot;
                  {{ $tc("players.count", event.player_ids.length, {count: event.player_ids.length}) }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-chip
                  class="ml-2 mb-1"
                  label
                  :color="eventTypes[event.event_type].color"
                  dark
                  small
                >
                  {{ eventTypes[event.event_type].text }}
                </v-chip>
              </v-list-item-action>
            </v-list-item>
          </v-list>
          <v-row
            v-else-if="!events.loading"
            justify="center"
          >
            <v-col cols="auto">
              {{ $t("select_event.no_events_match_filter") }}
            </v-col>
          </v-row>
        </div>
      </v-card-text>
    </card-dialog>
  </div>
</template>

<script>
import { CoachApiService, EventApiService, PlayerPositionApiService, PlayerStatusApiService } from '@/data/services'
import PerformanceGeneralTable from '@/components/performance/PerformanceGeneralTable'
import StatCard from '@/components/StatCard'
import { getColorForValue, processMaxValues, toCamelCase } from '@/utils/helpers'
import { EventType, EventTypes } from '@/data/models'
import PerformanceGraphs from '@/components/performance/PerformanceGraphs'
import dayjs from 'dayjs'
import debounce from 'lodash/debounce'
import PerformanceHeartRateTable from '@/components/performance/PerformanceHeartRateTable.vue'
import PerformanceHeartRateGraphs from '@/components/performance/PerformanceHeartRateGraphs.vue'
import {
  maxValuesPerformanceMatch,
  maxValuesPerformanceTraining,
  PerformanceTableHeaders,
  reversedVariables
} from '@/data/models/TableHeader'
import { cloneDeep, orderBy } from 'lodash'
import EventSidebar from '@/components/events/EventSidebar.vue'
import PerformancePeriodTable from '@/components/performance/PerformancePeriodTable.vue'
import PerformancePeriodGraphs from '@/components/performance/PerformancePeriodGraphs.vue'
import PerformanceRRTable from '@/components/performance/PerformanceRRTable.vue'
import PerformanceRRGraphs from '@/components/performance/PerformanceRRGraphs.vue'
import CardDialog from '@/components/helpers/CardDialog.vue'
import i18n from '@/plugins/i18n'
import PerformanceRawDialog from '@/components/performance/PerformanceRawDialog.vue'
import PerformanceCoachCustomize from '@/components/performance/PerfomanceCoachCustomize.vue'
import { DashboardType } from '@/data/models/Dashboard'
import PerformanceRpeTable from '@/components/performance/PerformanceRpeTable.vue'
import PerformanceIndividual from '@/components/performance/PerformanceIndividual.vue'
import VariableSelector from '@/components/performance/VariableSelector.vue'
import PerformanceSummaryGraphs from '@/components/performance/PerformanceSummaryGraphs.vue'
import PerformanceRpeGraphs from '@/components/performance/PerformanceRpeGraphs.vue'
import { EventBus } from '@/plugins/bus'
import ExerciseListItem from '@/components/training-builder/ExerciseListItem.vue'
import Pagination from '@/components/Pagination.vue'

export default {
  name: 'PerformanceEventView',
  // eslint-disable-next-line vue/no-unused-components
  components: {
    Pagination,
    ExerciseListItem,
    PerformanceRpeGraphs,
    PerformanceSummaryGraphs,
    VariableSelector,
    PerformanceIndividual,
    PerformanceRpeTable,
    PerformanceCoachCustomize,
    PerformanceRawDialog,
    CardDialog,
    PerformanceRRGraphs,
    EventSidebar,
    PerformanceRRTable,
    PerformancePeriodGraphs,
    PerformancePeriodTable,
    PerformanceHeartRateGraphs,
    PerformanceHeartRateTable,
    PerformanceGraphs,
    StatCard,
    PerformanceGeneralTable
  },
  data: () => ({
    currentView: 'SUMMARY',
    advancedView: 'period',
    periodSummary: false,
    eventSelection: false,
    periodSelection: false,
    date: dayjs().format('YYYY-MM-DD'),
    events: {
      loading: false,
      data: undefined,
      selectedEvent: undefined,
      pagination: {
        per_page: 10,
        current_page: 1,
        last_page: null
      }
    },
    eventDetails: {
      loading: false,
      data: undefined,
      periods: [],
      maxValues: {},
      selectedPeriodId: undefined,
      selectedExercise: undefined,
      drawer: false
    },
    eventParentDetails: {
      loading: false,
      data: undefined,
      periods: [],
      maxValues: {},
      selectedPeriodId: undefined,
      selectedExercise: undefined,
      drawer: false
    },
    player: {
      selected: undefined,
      rawSelected: undefined,
      tab: 'sprints',
      selectedBands: [3, 4, 5, 6, 7],
      loadingRawData: false
    },
    playerStatuses: {
      data: [],
      loading: false,
      loaded: false,
      selectedId: undefined
    },
    playerPositions: {
      data: [],
      loading: false,
      loaded: false,
      selectedId: undefined
    },
    eventPeriods: {
      data: [],
      loading: false,
      maxValues: {}
    },
    variables: {
      data: null,
      loading: false
    },
    teamStats: {
      data: [],
      loading: false
    },
    playerName: '',
    filters: {
      eventType: undefined,
      dateFrom: dayjs().subtract(3, 'month').format('YYYY-MM-DD'),
      dateTo: dayjs().format('YYYY-MM-DD'),
      eventName: ''
    },
    filterDateFromActive: false,
    filterDateToActive: false,
    playerIndividualDialog: false,
    tableHeadersSelection: false,
    sprintBandsColors: ['#FFBBBB', '#FFAAAA', '#FF9999', '#FF8888', '#FF7777', '#FF6666', '#FF5555'],
    accelerationBands: ['a < -4m/s²', '-4m/s² < a < -3m/s²', '-3m/s² < a < -2m/s²', '-2m/s² < a < 0', '0 < a < 2m/s²', '2m/s² < a < 3m/s²', '3m/s² < a < 4m/s²', 'a > 4m/s²'],
    sprintBands: ['0km/h < v < 7.2km/h', '7.2km/h < v < 14.4km/h', '14.4km/h < v < 21.6km/h', '21.6km/h < v < 25.2km/h', '25.2km/h < v < 28.8km/h', '28.8km/h < v < 32.4km/h', 'v > 32.4km/h'],
    hrBands: ['20 - 45', '45 - 55', '55 - 65', '65 - 75', '75 - 85', '85 - 95', '95 - 105', '105 - 200'],
    rawDialog: false,
    tableHeaders: Object.assign({}, PerformanceTableHeaders),
    colorMaxValuesTraining: maxValuesPerformanceTraining,
    colorMaxValuesMatch: maxValuesPerformanceMatch,
    customizeModal: false,
    dashboard: DashboardType.EVENT_PERFORMANCE
  }),
  computed: {
    DashboardType() {
      return DashboardType
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    views() {
      return [
        {
          name: 'SUMMARY',
          title: this.$t('performance.event.summary'),
          active: true
        },
        {
          name: 'RPE',
          title: this.$t('performance.event.rpe'),
          active: this.user.organisation_settings.has_itl_module && (this.eventParentDetails.data?.has_rpe_stats || this.eventParentDetails.data?.has_rpe_stats === null)
        },
        {
          name: 'GPS',
          title: this.$t('performance.event.gps'),
          active: this.user.organisation_settings.has_gps_module && (this.eventParentDetails.data?.has_gps_stats || this.eventParentDetails.data?.has_gps_stats === null)
        },
        {
          name: 'HR',
          title: this.$t('performance.event.hr'),
          active: this.user.organisation_settings.has_heart_rate_module && (this.eventParentDetails.data?.has_hr_stats || this.eventParentDetails.data?.has_hr_stats === null)
        },
        {
          name: 'RR',
          title: this.$t('performance.event.rr'),
          active: this.user.organisation_settings.has_heart_rate_module && (this.eventParentDetails.data?.has_rr_stats || this.eventParentDetails.data?.has_rr_stats === null)
        }
      ].filter(view => view.active)
    },
    currentTeamStats() {
      if (this.periodSummary) {
        return this.eventParentDetails?.data?.teams?.[0]?.team_stats || {}
      }

      if (this.eventDetails.selectedExercise && this.eventDetails.data.group) {
        return this.eventDetails.data.group || {}
      }

      if (!this.eventDetails.data || !this.eventDetails.data.teams || !this.eventDetails.data.teams[0].team_stats) return {}

      return this.eventDetails.data.teams[0].team_stats
    },
    currentTeam() {
      return this.$store.getters[this.$storeGetters.APP_CURRENT_TEAM]
    },
    isIndividual() {
      return this.eventDetails.data && this.eventDetails.data.players.length === 1
    },
    hasPeriods() {
      return this.eventParentDetails.data && this.eventParentDetails.data.periods.length > 0
    },
    hasTrainingExercisesStartTime() {
      if (this.eventDetails.data && this.eventDetails.data.players.length > 1) {
        return this.eventParentDetails.data?.training_exercises?.some(exercise => {
          if (exercise.exercise_start_at) {
            return true
          }
          if (exercise.type === 'group' && exercise.exercises?.some(subExercise => subExercise.exercise_start_at)) {
            return true
          }
        })
      } else if (this.eventDetails.data && this.eventDetails.data.players.length === 1) {
        return this.eventParentDetails.data?.players[0].training_exercises?.some(exercise => {
          if (exercise.exercise_start_at) {
            return true
          }
          if (exercise.type === 'group' && exercise.exercises?.some(subExercise => subExercise.exercise_start_at)) {
            return true
          }
        })
      }
      return false
    },
    filteredPlayers() {
      const filters = {
        SUMMARY: p => !!p.player_stats || p.has_attended === false,
        RPE: p => !!p.player_stats || p.has_attended === false,
        GPS: p => !!p.player_stats?.UXPCSTD,
        HR: p => !!p.player_stats?.HRMAX,
        RR: p => !!p.player_stats?.SDNN
      }

      return this.eventDetails.data ? this.eventDetails.data.players.filter(filters[this.currentView] || (() => false)) : []
    },
    searchFilteredPlayers() {
      return this.filteredPlayers.filter(player => {
        if (this.playerPositions.selectedId) return player.player_positions.map(p => p.id).indexOf(this.playerPositions.selectedId) > -1

        return player
      }).filter(player => {
        if (this.playerStatuses.selectedId) return player.player_status_id === this.playerStatuses.selectedId

        return player
      }).filter(player => {
        if (this.playerName) return (player.first_name + player.last_name).toLowerCase().includes(this.playerName.toLowerCase())

        return player
      })
    },
    eventTrainingExercises() {
      let exercises = []
      if (this.eventParentDetails.data && this.eventParentDetails.data.players.length > 1) {
        exercises = this.eventParentDetails.data.training_exercises
      } else if (this.eventParentDetails.data && this.eventParentDetails.data.players.length === 1) {
        exercises = this.eventParentDetails.data.players[0].training_exercises
      }

      const result = []

      exercises.forEach(exercise => {
        if (exercise.type === 'group' && Array.isArray(exercise.exercises)) {
          result.push(...exercise.exercises)
        } else {
          result.push(exercise)
        }
      })

      return result
    },
    integrations() {
      return this.user.organisation_settings.integrations || []
    },
    performanceMaxValues() {
      return (this.eventDetails.data && this.eventDetails.data.event_type === 'training') ? this.colorMaxValuesTraining : this.colorMaxValuesMatch
    },
    dateFromText() {
      return this.$dayjs(this.filters.dateFrom).format('ll')
    },
    dateToText() {
      return this.$dayjs(this.filters.dateTo).format('ll')
    },
    teams() {
      return this.$store.getters[this.$storeGetters.APP_TEAMS]
    },
    statCardsSUM() {
      return this.variables.data ? orderBy(this.variables.data.team.SUMMARY.filter(tableHeader => tableHeader.selected), ['order']) : []
    },
    statCardsRPE() {
      return this.variables.data ? orderBy([...this.variables.data.team.RPE.filter(tableHeader => tableHeader.selected), { value: 'feedback', text: this.$t('variables.feedback') }], ['order']) : []
    },
    statCardsGPS() {
      return this.variables.data ? orderBy(this.variables.data.team.GPS.filter(tableHeader => tableHeader.selected), ['order']) : []
    },
    statCardsHR() {
      return this.variables.data ? orderBy(this.variables.data.team.HR.filter(tableHeader => tableHeader.selected), ['order']) : []
    },
    statCardsRR() {
      return this.variables.data ? orderBy(this.variables.data.team.RR.filter(tableHeader => tableHeader.selected), ['order']) : []
    },
    tableHeadersSUM() {
      return this.variables.data ? [{ value: 'last_name', text: this.$t('table_headers.name') }].concat(orderBy(this.variables.data.player.SUMMARY.filter(tableHeader => tableHeader.selected), ['order'])) : []
    },
    tableHeadersRPE() {
      return this.variables.data ? [{ value: 'last_name', text: this.$t('table_headers.name') }].concat(orderBy(this.variables.data.player.RPE.filter(tableHeader => tableHeader.selected), ['order'])) : []
    },
    tableHeadersGPS() {
      return this.variables.data ? [{ value: 'last_name', text: this.$t('table_headers.name') }].concat(orderBy(this.variables.data.player.GPS.filter(tableHeader => tableHeader.selected), ['order'])) : []
    },
    tableHeadersHR() {
      return this.variables.data ? [{ value: 'last_name', text: this.$t('table_headers.name') }].concat(orderBy(this.variables.data.player.HR.filter(tableHeader => tableHeader.selected), ['order'])) : []
    },
    tableHeadersRR() {
      return this.variables.data ? [{ value: 'last_name', text: this.$t('table_headers.name') }].concat(orderBy(this.variables.data.player.RR.filter(tableHeader => tableHeader.selected), ['order'])) : []
    },
    playerVariables() {
      if (!this.variables.data) return []

      return this.variables.data.player[this.currentView]
    },
    buttonLabel() {
      if (this.eventDetails.selectedExercise) {
        return this.eventDetails.selectedExercise.name || 'Superset'
      } else if (this.eventDetails.selectedPeriodId === -1) {
        return this.$t('card_title.period_summary')
      } else if (this.eventDetails.selectedPeriodId === 0 || this.eventDetails.selectedPeriodId === undefined) {
        return this.$t('card_dialog.select_period.entire_event', { event_type: this.eventTypes[this.eventParentDetails.data.event_type].text })
      } else {
        return this.eventParentDetails.periods.find(p => p.id === this.eventDetails.selectedPeriodId)?.title
      }
    }
  },
  watch: {
    'events.selectedEvent': function (val) {
      if (val) this.fetchEventDetails()
    },
    'eventDetails.selectedPeriodId': function (val) {
      if (val === undefined) return

      this.eventDetails.selectedExercise = undefined
      if (val === -1) {
        this.periodSummary = true
        this.fetchAllPeriods()
      } else if (val === 0) {
        this.periodSummary = false
        this.loadPeriod()
      } else {
        this.periodSummary = false
        this.loadPeriod(this.eventDetails.selectedPeriodId)
      }
    },
    'eventDetails.selectedExercise': function (val) {
      if (val) {
        this.eventDetails.selectedPeriodId = undefined
        this.fetchStatsByExercise()
      }
    },
    currentTeam() {
      this.fetchEvents(true)
    },
    'filters.eventName': function() {
      this.fetchEventsDebounced()
    },
    'filters.eventType': function () {
      this.fetchEvents()
    },
    'eventParentDetails.data': function () {
      if (!this.hasPeriods && this.hasTrainingExercisesStartTime) this.advancedView = 'exercise'
    },
    'events.pagination.current_page': function (val) {
      this.fetchEvents()
    }
  },
  mounted() {
    EventBus.$on(this.$events.EVENT_DELETED, (event, shouldFetch = true) => {
      if (shouldFetch) this.fetchEvents(true)
    })

    EventBus.$on(this.$events.EVENT_UPDATED, (event) => {
      if (this.eventDetails.data && this.eventDetails.data.id === event.id) {
        this.fetchEventDetails()
      }
    })
  },
  created() {
    this.eventTypeMatch = EventType.MATCH
    this.eventTypeTraining = EventType.TRAINING

    this.eventTypes = EventTypes

    this.mainEventTypesList = Object.keys(EventTypes).map(k => {
      return {
        ...EventTypes[k],
        key: k
      }
    }).filter(e => e.is_other === false)
    this.otherEventTypesList = Object.keys(EventTypes).map(k => {
      return {
        ...EventTypes[k],
        key: k
      }
    }).filter(e => e.is_other === true)

    if (this.$route.query.event_id) {
      this.events.selectedEvent = {
        id: parseInt(this.$route.query.event_id)
      }
      this.$router.replace({ query: {} })

      this.fetchEvents()
    } else {
      this.fetchEvents(true)
    }

    this.fetchVariables()
    this.fetchPlayerPositions()
    this.fetchPlayerStatuses()

    this.sprintHeaders = [
      { value: 'start_time_min', text: i18n.t('table_headers.performance.player_details.efforts.start_time') },
      { value: 'end_time_min', text: i18n.t('table_headers.performance.player_details.efforts.end_time') },
      { value: 'band', text: i18n.t('table_headers.performance.player_details.efforts.band') },
      { value: 'distance', text: i18n.t('table_headers.performance.player_details.efforts.distance') },
      { value: 'duration_min', text: i18n.t('training_builder.text_input.duration.title') },
      { value: 'max', text: 'Vmax' },
      { value: 'average', text: 'Vavg' },
      { value: 'one_seconds_before', text: 'T-1s' },
      { value: 'two_seconds_before', text: 'T-2s' },
      { value: 'three_seconds_before', text: 'T-3s' }
    ]

    this.accHeaders = [
      { value: 'start_time_min', text: i18n.t('table_headers.performance.player_details.efforts.start_time') },
      { value: 'end_time_min', text: i18n.t('table_headers.performance.player_details.efforts.end_time') },
      { value: 'band', text: i18n.t('table_headers.performance.player_details.efforts.band') },
      { value: 'distance', text: i18n.t('table_headers.performance.player_details.efforts.distance') },
      { value: 'duration_min', text: i18n.t('training_builder.text_input.duration.title') },
      { value: 'max', text: i18n.t('table_headers.performance.player_details.efforts.peek') },
      { value: 'average', text: i18n.t('table_headers.performance.player_details.efforts.avg_acc') },
      { value: 'one_seconds_before', text: 'T-1s' },
      { value: 'two_seconds_before', text: 'T-2s' },
      { value: 'three_seconds_before', text: 'T-3s' }
    ]

    this.hrHeaders = [
      { value: 'start_time', text: 'Start Time' },
      { value: 'end_time', text: 'End Time' },
      { value: 'band', text: 'Band' },
      { value: 'distance', text: 'Distance' },
      { value: 'max', text: 'Max HR' },
      { value: 'average', text: 'Avg HR' }
    ]
  },
  beforeDestroy() {
    EventBus.$off(this.$events.EVENT_DELETED)
    EventBus.$off(this.$events.EVENT_UPDATED)
  },
  methods: {
    dayjs,
    reversedVariables() {
      return reversedVariables
    },
    cloneDeep,
    orderBy,
    fetchEvents: async function (forceFirst = false, type = EventType.TRAINING) {
      if (forceFirst) this.eventDetails.data = undefined
      this.events.loading = true

      try {
        const response = await EventApiService.getAll({
          order_by: 'start_at',
          order: 'desc',
          only_own_events: true,
          from: this.filters.dateFrom,
          to: this.filters.dateTo,
          team_id: this.currentTeam.id,
          search: this.filters.eventName,
          type: this.filters.eventType,
          compact: true,
          per_page: this.events.pagination.per_page,
          page: this.events.pagination.current_page
        })

        this.events.data = response.data
        this.events.pagination = response.meta

        if (forceFirst && this.events.data.length > 0) {
          this.events.selectedEvent = this.events.data[0]
        }
      } catch (e) {
        this.events.data = []
        throw e
      } finally {
        this.events.loading = false
      }
    },
    fetchEventsDebounced: debounce(function () {
      this.fetchEvents()
    }, 500),
    async fetchEventDetails(periodId = null, closeEventSidebar = true) {
      this.eventDetails.loading = true
      this.eventDetails.data = undefined
      this.eventDetails.selectedExercise = undefined
      if (closeEventSidebar) this.eventParentDetails.drawer = false
      this.advancedView = 'period'

      try {
        const response = await EventApiService.getEventDetails(periodId || this.events.selectedEvent.id)

        const data = response.data

        this.eventDetails.data = data

        if (!periodId) {
          this.eventDetails.periods = data.periods
        }

        this.eventDetails.maxValues = processMaxValues(response.data.players.map(player => player.player_stats || {}))

        if (!periodId || !this.eventParentDetails.data || (this.eventParentDetails.data.id === periodId)) {
          this.eventParentDetails.data = cloneDeep(this.eventDetails.data)
          this.eventParentDetails.periods = cloneDeep(this.eventDetails.periods)
          this.eventParentDetails.maxValues = cloneDeep(this.eventDetails.maxValues)
        }
      } catch (e) {
        this.events.data = []
        throw e
      } finally {
        this.eventDetails.loading = false
        this.eventParentDetails.loading = false
      }
    },
    getColorFromValue(value, max, reverse = false) {
      return getColorForValue(value, max, reverse)
    },
    loadPeriod(periodId = null) {
      this.eventDetails.selectedPeriodId = periodId || undefined

      setTimeout(() => {
        this.fetchEventDetails(periodId || undefined)
      }, 200)
    },
    async fetchPlayerPositions() {
      if (this.playerPositions.loaded) return

      try {
        this.playerPositions.loading = true

        const response = await PlayerPositionApiService.getAll()

        this.playerPositions.data = response.data.map((position) => {
          return {
            id: position.id,
            name: this.$t(`player_position.${toCamelCase(position.name)}`),
            short: this.$t(`player_position.${toCamelCase(position.name)}.short`)
          }
        })
        this.playerPositions.loaded = true
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })
      } finally {
        this.playerPositions.loading = false
      }
    },
    async fetchPlayerStatuses() {
      if (this.playerStatuses.loaded) return

      try {
        this.playerStatuses.loading = true

        const response = await PlayerStatusApiService.getAll()

        this.playerStatuses.data = response.data.map((status) => {
          return {
            id: status.id,
            name: this.$t(`player_status.${status.name}`)
          }
        })
        this.playerStatuses.loaded = true
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })
      } finally {
        this.playerStatuses.loading = false
      }
    },
    async fetchAllPeriods() {
      this.eventPeriods.loading = true
      this.eventPeriods.data = []
      const data = []
      for (const period in this.eventParentDetails.data.periods) {
        try {
          const response = await EventApiService.getEventDetails(this.eventParentDetails.data.periods[period].id)
          data.push(response.data)
        } catch (e) {
          this.eventPeriods.data = []
          throw e
        }
      }
      this.eventPeriods.maxValues = processMaxValues(data.map(period => period.teams[0].team_stats), 1.1)
      this.eventPeriods.data = data
      this.eventPeriods.loading = false
    },
    async fetchStatsByExercise() {
      if (this.eventDetails.loading) return

      try {
        this.eventDetails.loading = true

        const params = {
          player_ids: this.eventDetails.data.players.map(p => p.id),
          event_ids: [this.eventParentDetails.data.id],
          training_protocol_exercise_ids: this.eventDetails.selectedExercise.type === 'superset' ? [this.eventDetails.selectedExercise.exercises[0].internal_id] : [this.eventDetails.selectedExercise.internal_id]
        }

        const result = await EventApiService.getAdvancedPerformanceStats(params)

        this.eventDetails.data = result

        this.eventDetails.maxValues = processMaxValues(result.players.map(player => player.player_stats || {}))
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_advanced_statistics'),
          color: 'error'
        })

        throw e
      } finally {
        this.eventDetails.loading = false
      }
    },
    async fetchVariables() {
      this.variables.loading = true

      try {
        this.variables.data = await CoachApiService.getVariables(this.user.coach.id, {
          dashboard: this.dashboard
        })

        Object.keys(this.variables.data).forEach(group => {
          Object.keys(this.variables.data[group]).forEach(category => {
            this.variables.data[group][category] = this.variables.data[group][category].map(v => {
              return {
                ...v,
                text: v.suffix ? this.$t(`variables.${v.value}`) + ' (' + this.$t(`suffixes.${v.suffix}`) + ')' : this.$t(`variables.${v.value}`),
                long_text: v.suffix ? this.$t(`variables.${v.value}.long`) + ' (' + this.$t(`suffixes.${v.suffix}`) + ')' : this.$t(`variables.${v.value}.long`)
              }
            })
          })
        })
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_variables.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.variables.loading = false
      }
    },
    selectPrevious() {
      const playerIndex = this.eventDetails.data.players.indexOf(this.player.rawSelected)
      if (playerIndex === 0) return
      this.player.rawSelected = this.eventDetails.data.players[playerIndex - 1]
    },
    selectNext() {
      const playerIndex = this.eventDetails.data.players.indexOf(this.player.rawSelected)
      if (playerIndex === this.eventDetails.data.players.length) return
      this.player.rawSelected = this.eventDetails.data.players[playerIndex + 1]
    },
    updateOrder(event) {
      this.playerVariables.filter(v => v.selected).forEach((variable, index) => {
        variable.order = index + 1
      })
    }
  }
}
</script>

<style scoped>

</style>
